<template>
  <Transition name="fade">
    <div v-if="isVisible" :class="wrapperClass">
      <div class="relative z-20 flex items-center justify-center" :class="positioned ? 'px-3 pb-3 pt-2 sm:px-10' : ''">
        <div
          :class="containerClass"
          class="toast-container relative flex items-center gap-4 overflow-hidden rounded-2xl border-2"
          data-test="toast-banner"
        >
          <div
            class="toast-icon flex h-full w-16 flex-shrink-0 items-center justify-center self-stretch"
            :class="iconWrapClass"
          >
            <InlineSvg :path="displayIconPath" :class="textClass" class="h-6 w-6" />
          </div>
          <p :class="textClass" class="text-medium mb-0 min-w-0 flex-1 py-3 pr-1 leading-5" data-test="toast-message">
            <slot>{{ currentMessage.text }}</slot>
          </p>
          <button
            class="flex-shrink-0 pr-4"
            type="button"
            :aria-label="dismissLabel"
            data-test="toast-dismiss"
            @click="dismiss"
          >
            <InlineSvg path="icons/close" :class="textClass" class="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import InlineSvg from './InlineSvg'

const props = defineProps({
  message: { type: Object, default: null },
  variant: { type: String, default: 'success' },
  iconPath: { type: String, default: null },
  autoDismissMs: { type: Number, default: 6000 },
  dismissLabel: { type: String, default: 'dismiss toast' },
  positioned: { type: Boolean, default: true },
})

const emit = defineEmits(['dismiss'])

const isVisible = ref(false)
const currentMessage = ref({ text: '', type: 'success' })
let timeoutId = null

const STATES = {
  success: {
    container: 'border-green-200 bg-green-50',
    iconWrap: 'bg-green-200',
    text: 'text-green-800',
    icon: 'icons/check',
  },
  warning: {
    container: 'border-orange-300 bg-orange-50',
    iconWrap: 'bg-orange-300',
    text: 'text-orange-800',
    icon: 'icons/exclamation',
  },
  error: {
    container: 'border-red-300 bg-red-50',
    iconWrap: 'bg-red-300',
    text: 'text-red-800',
    icon: 'icons/exclamation',
  },
  reward: {
    container: 'border-none bg-blue-900',
    iconWrap: 'bg-accent',
    text: 'text-white',
    icon: 'icons/star',
  },
}

const dismiss = () => {
  isVisible.value = false
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
  emit('dismiss', currentMessage.value)
}

const show = () => {
  isVisible.value = true
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
  if (props.autoDismissMs > 0) {
    timeoutId = setTimeout(() => {
      dismiss()
    }, props.autoDismissMs)
  }
}

watch(
  () => props.message,
  (newMessage) => {
    if (newMessage) {
      currentMessage.value = {
        ...newMessage,
        type: newMessage.type || props.variant,
      }
      show()
    }
  },
  { immediate: true },
)

const stateInfo = computed(() => STATES[currentMessage.value.type] || STATES.error)
const containerClass = computed(() => stateInfo.value.container)
const iconWrapClass = computed(() => stateInfo.value.iconWrap)
const textClass = computed(() => stateInfo.value.text)
const displayIconPath = computed(() => props.iconPath || stateInfo.value.icon)
const wrapperClass = computed(() =>
  props.positioned
    ? 'thinksphere-toast z-100 fixed left-1/2 w-full max-w-xl -translate-x-1/2 transform print:hidden'
    : 'w-full print:hidden',
)
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.toast-container,
.toast-icon {
  min-height: stretch;
}
</style>
